import { useEffect, useMemo, useState } from 'react';
import { createPortal } from 'react-dom';
import { useLocation, useNavigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { toast } from 'react-toastify';
import {
  hydrateFromReport,
  toggleChecked,
} from '../store/adviceSlice';
import { fetchBloodSummary } from '../services/blood';
import { approveOrder, fetchOrderImages } from '../services/order';
import OrangeArrow from '../assets/icons/OrangeArrow.svg';
import PurpleArrow from '../assets/icons/PurpleArrow.svg';
import DropPlus from '../assets/icons/DropPlus.svg';
import Bullseye from '../assets/icons/bullseye-arrow .svg';
import ProductImg from '../assets/images/product.svg';
import {
  formatEuroNl,
  getWebInfoBodyText,
  roundMoneyToCents,
  stripWebInfoTitle,
} from '../utils/helpers';
import { SHIPPING_AND_FULFILMENT } from '../utils/constants';

export default function YourAdvice() {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [activeOrderId, setActiveOrderId] = useState('');
  const [hoveredSupplementId, setHoveredSupplementId] = useState(null);
  const [tooltipPosition, setTooltipPosition] = useState({ top: 0, left: 0 });
  const [productImages, setProductImages] = useState({});
  const location = useLocation();
  const reportContext = location?.state?.reportContext || null;
  const userName = useSelector(
    (s) => s.user.fullName || localStorage.getItem('userFullName') || 'Iryna'
  );
  const advice = useSelector((s) => s.advice);
  const hasBloodSummaryPoints =
    Array.isArray(advice.bloodSummary.points) &&
    advice.bloodSummary.points.length > 0;
  const items = advice.supplements;
  const isApproved = advice.isApproved; // kept but not used for row-level locking anymore
  const supPeriodId = advice.supPeriodId;
  const approvedIdSet = useMemo(() => {
    const ids = Array.isArray(advice.approvedSupplements)
      ? advice.approvedSupplements.filter((x) => !!x).map((x) => String(x))
      : [];
    return new Set(ids);
  }, [advice.approvedSupplements]);
  const isItemApproved = (it) => isApproved && approvedIdSet.has(String(it.id));
  const isItemSelectable = (it) =>
    isApproved ? !approvedIdSet.has(String(it.id)) : true;
  const selectionAwareSubtotal = useMemo(
    () =>
      items.reduce((sum, item) => {
        const approved = isItemApproved(item);
        const selected = item.checked;
        return approved || selected ? sum + item.price * item.qty : sum;
      }, 0),
    [items, isApproved, approvedIdSet]
  );
  const shippingExVat = useMemo(() => {
    const shipping =
      selectionAwareSubtotal > 0 ? SHIPPING_AND_FULFILMENT : 0;
    return roundMoneyToCents(shipping);
  }, [selectionAwareSubtotal]);
  const vatOnShipping = useMemo(
    () => roundMoneyToCents(shippingExVat * 0.21),
    [shippingExVat]
  );
  const grandTotal = useMemo(
    () =>
      roundMoneyToCents(
        selectionAwareSubtotal + shippingExVat + vatOnShipping
      ),
    [selectionAwareSubtotal, shippingExVat, vatOnShipping]
  );
  const toggle = (id) => {
    if (approvedIdSet.has(String(id))) return;
    dispatch(toggleChecked(id));
  };

  const getSupplementImage = (item) => {
    const sku = String(item?.sku || item?.productSku || '').trim();
    if (sku && productImages[sku]) return productImages[sku];
    const directImage = String(item?.image || '').trim();
    return directImage || null;
  };

  const getDescriptionPreview = (webInfo) => getWebInfoBodyText(webInfo);

  const hasDescriptionBody = (webInfo) => Boolean(getDescriptionPreview(webInfo));

  const showDescriptionTooltip = (item, e) => {
    if (!hasDescriptionBody(item?.webInfo)) return;
    const titleEl = e.currentTarget.querySelector('h3');
    const rect = titleEl
      ? titleEl.getBoundingClientRect()
      : e.currentTarget.getBoundingClientRect();
    setTooltipPosition({
      top: rect.bottom + 16,
      left: rect.left,
    });
    setHoveredSupplementId(item.id);
  };

  const handleApproveClick = () => {
    if (loading || isApproved) return;
    setShowConfirmModal(true);
  };

  const handleCancelApprove = () => {
    setShowConfirmModal(false);
  };

  const handleApprove = async () => {
    const selectedProductIds = (items || [])
      .filter((item) => item?.checked)
      .map((item) => String(item?.id || '').trim())
      .filter((id) => id.length > 0);
    if (selectedProductIds.length === 0) {
      toast.error('Please select at least one product to approve.');
      return;
    }
    const orderId = String(
      reportContext?.orderApprovalId ||
        reportContext?.order_id ||
        reportContext?.orderId ||
        activeOrderId ||
        ''
    ).trim();
    if (!orderId) {
      toast.error('Unable to approve: missing order id for this advice.');
      return;
    }
    try {
      setShowConfirmModal(false);
      setLoading(true);
      const response = await approveOrder(orderId, {
        productIds: selectedProductIds,
      });

      if (
        response?.success &&
        response?.data &&
        response.data.message &&
        response.data.message.includes('out of stock') &&
        !response.data.is_approved
      ) {
        toast.warning(response.data.message);
        return;
      }

      const checkoutUrl = String(response?.data?.checkout_url || '').trim();
      if (checkoutUrl) {
        toast.info('Redirecting to payment...');
        window.location.href = checkoutUrl;
        return;
      }

      if (
        response?.success &&
        response?.data &&
        (response.data.payment_status || response.data.is_approved !== undefined)
      ) {
        toast.success('Order approved successfully');
      }
    } catch (e) {
      toast.error(e?.message || 'Failed to approve order. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    let cancelled = false;
    async function loadFreshData() {
      try {
        setLoading(true);
        // Always fetch fresh on mount and pick the intended record if provided
        const mapped = await fetchBloodSummary();
        if (cancelled) return;
        if (Array.isArray(mapped) && mapped.length) {
          const candidates = mapped.slice();

          // If we were navigated with a specific report context, honor it
          if (reportContext) {
            const byId = candidates.find(
              (r) =>
                (reportContext.recordId &&
                  r.recordId === reportContext.recordId) ||
                (reportContext.orderApprovalId &&
                  (r.orderApprovalId || r.order_id) ===
                    reportContext.orderApprovalId) ||
                (reportContext.order_id &&
                  (r.orderApprovalId || r.order_id) === reportContext.order_id) ||
                (reportContext.orderId &&
                  r.orderId === reportContext.orderId) ||
                (reportContext.supPeriodId &&
                  r.supPeriodId === reportContext.supPeriodId)
            );
            if (byId) {
              setActiveOrderId(
                String(byId.orderApprovalId || byId.order_id || '').trim()
              );
              dispatch(hydrateFromReport(byId));
              return;
            }
          }
          const withSupps = candidates.filter(
            (r) => Array.isArray(r.supplements) && r.supplements.length > 0
          );
          const withPeriod = candidates.filter((r) => !!r.supPeriodId);
          let preferred = null;
          if (withSupps.length > 0) {
            preferred = withSupps;
          } else if (withPeriod.length > 0) {
            preferred = withPeriod;
          } else {
            preferred = candidates;
          }
          preferred.sort((a, b) => {
            const aDate = new Date(a?.header?.resultDate || 0).getTime();
            const bDate = new Date(b?.header?.resultDate || 0).getTime();
            return bDate - aDate;
          });
          if (preferred[0]) {
            setActiveOrderId(
              String(
                preferred[0].orderApprovalId || preferred[0].order_id || ''
              ).trim()
            );
            dispatch(hydrateFromReport(preferred[0]));
          }
        }
      } catch (e) {
        // Error handling is done via toast notifications
      } finally {
        if (!cancelled) setLoading(false);
      }
    }
    loadFreshData();
    return () => {
      cancelled = true;
    };
  }, [dispatch, reportContext]);

  useEffect(() => {
    const orderId = String(activeOrderId || '').trim();
    if (!orderId) return;

    let cancelled = false;

    async function loadImages() {
      try {
        const images = await fetchOrderImages(orderId);
        if (cancelled || !Array.isArray(images)) return;

        const imageMap = {};
        images.forEach((item) => {
          if (item?.sku && item?.image_url) {
            imageMap[String(item.sku)] = item.image_url;
          }
        });
        setProductImages(imageMap);
      } catch {
        // Images are optional; keep placeholders on failure.
      }
    }

    loadImages();
    return () => {
      cancelled = true;
    };
  }, [activeOrderId]);

  return (
    <div className="w-full max-w-[1320px] mx-auto px-4 sm:px-6">
      <h1 className="text-[#2B3133] font-libreBaskerville text-2xl md:text-[40px] text-center mb-6 md:mb-8">
        Supplements Advice for {userName}
      </h1>

      {loading ? (
        <div className="flex items-center justify-center py-16">
          <div className="animate-spin h-8 w-8 border-4 border-[#C1DCD4] border-t-[#119975] rounded-full" />
        </div>
      ) : items.length === 0 ? (
        <div className="flex items-center justify-center px-6 min-h-[60vh]">
          <div className="text-center max-w-md">
            <h2 className="mb-4 text-3xl md:text-4xl font-libreBaskerville font-semibold text-[#119975]">
              No advice to show
            </h2>
            <p className="text-[#889696] font-poppins text-sm md:text-lg">
              We couldn&apos;t find any advice for this user yet. Please check
              back later.
            </p>
            <button
              type="button"
              onClick={() => navigate('/medical-questioning')}
              className="mt-6 h-11 px-7 rounded-full bg-[#119975] text-white font-poppins text-sm font-medium hover:bg-[#0f8a6b] transition-colors"
            >
              Start Medical Questionnaire
            </button>
          </div>
        </div>
      ) : (
        <div className="space-y-4 md:space-y-6">
          <div>
            <div className="flex items-center gap-2 mb-2">
              <img src={DropPlus} alt="blood" className="w-6 h-6" />
              <span className="text-[#2B3133] font-poppins font-medium">
                Blood Test Summary
              </span>
            </div>
            <div className="text-[#889696] font-poppins text-xs sm:text-sm mb-3 flex flex-nowrap items-baseline gap-1.5 sm:gap-3 whitespace-nowrap overflow-hidden">
              <span>
                Blood test:{' '}
                <span className="text-[#1E2426]">
                  {advice.bloodSummary.testType}
                </span>
              </span>
              <span>
                Test date:{' '}
                <span className="text-[#1E2426]">
                  {advice.bloodSummary.testDate}
                </span>
              </span>
              <span>
                Result date:{' '}
                <span className="text-[#1E2426]">
                  {advice.bloodSummary.resultDate}
                </span>
              </span>
            </div>
            {hasBloodSummaryPoints && (
              <div
                className="rounded-xl mx-[-1px]"
                style={{ background: '#EEE8FF' }}
              >
                <div className="pl-4 pr-4 md:pl-6 md:pr-[22px] py-4 md:py-6 text-[#2B3133] font-poppins font-normal text-sm grid grid-cols-1 gap-y-3 md:flex md:flex-wrap md:gap-x-6 items-start leading-[1.5]">
                  {advice.bloodSummary.points.map((p, idx) => (
                    <div key={idx} className="flex items-start gap-3">
                      <img
                        src={PurpleArrow}
                        alt="arrow"
                        className="w-5 h-5 md:w-6 md:h-6 translate-x-[2px]"
                      />
                      <span className="translate-x-[2px]">{p}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>

          <div>
            <div className="flex items-center gap-2 mb-2">
              <img src={Bullseye} alt="goals" className="w-6 h-6" />
              <span className="text-[#2B3133] font-poppins font-medium">
                Lifestyle Goals
              </span>
            </div>
            <div
              className="rounded-xl mx-[-1px] mt-4"
              style={{ background: '#FFEFE1' }}
            >
              <div className="px-4 py-4 md:px-6 md:py-6 text-[#2B3133] font-poppins font-normal text-sm flex flex-wrap gap-6 md:gap-10 items-center">
                {advice.lifestyleGoals.map((g, idx) => (
                  <div key={idx} className="flex items-center gap-2">
                    <img
                      src={OrangeArrow}
                      alt="arrow"
                      className="w-6 h-6 translate-x-[2px]"
                    />
                    <span className="translate-x-[2px]">{g}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      )}

      {!loading && items.length > 0 && (
        <>
          <h2 className="text-[#2B3133] font-libreBaskerville font-normal text-2xl md:text-[36px] text-center mt-8 md:mt-10 mb-6">
            Your Advice
          </h2>

          <div className="md:hidden space-y-4">
            {items.map((i, idx) => {
              const itemApproved = isItemApproved(i);
              const supplementImage = getSupplementImage(i);
              const descriptionPreview = i.webInfo
                ? getDescriptionPreview(i.webInfo)
                : '';
              return (
                <div
                  key={i.id}
                  className={`bg-white rounded-xl border border-[#E5E7EB] p-4 shadow-sm ${itemApproved || i.checked ? '' : 'opacity-50'}`}
                >
                  <div className="flex items-center gap-3 mb-4">
                    {!isApproved && isItemSelectable(i) && !itemApproved && (
                      <input
                        type="checkbox"
                        checked={i.checked}
                        onChange={() => toggle(i.id)}
                        className="w-5 h-5 text-[#119975] rounded border-2 border-[#119975] focus:ring-[#119975] accent-[#119975]"
                      />
                    )}
                    <div className="w-12 h-12 bg-[#F5F5F5] rounded-lg flex items-center justify-center shrink-0">
                      {supplementImage ? (
                        <img
                          src={supplementImage}
                          alt={i.brand}
                          className="w-full h-full object-contain p-1 rounded-lg"
                        />
                      ) : (
                        <img
                          src={ProductImg}
                          alt="product"
                          className="w-full h-full object-contain p-1"
                        />
                      )}
                    </div>
                    <div
                      className="flex-1"
                      onMouseEnter={(e) => showDescriptionTooltip(i, e)}
                      onMouseLeave={() => setHoveredSupplementId(null)}
                    >
                      <h3
                        className={`text-[#2B3133] font-poppins text-lg font-semibold transition-colors ${hasDescriptionBody(i.webInfo) ? 'cursor-pointer hover:text-[#119975]' : ''}`}
                      >
                        {i.brand}
                      </h3>
                      {i.packagingLine && (
                        <p className="text-gray-500 text-xs mt-0.5">
                          {i.packagingLine}
                        </p>
                      )}
                      {descriptionPreview && (
                        <p className="text-[#889696] font-poppins text-xs leading-relaxed line-clamp-3 mt-1 cursor-pointer">
                          {descriptionPreview}
                        </p>
                      )}
                    </div>
                  </div>

                  <div className="space-y-3">
                    <div className="flex justify-between items-center py-2 border-b border-[#EDEEF0]">
                      <span className="text-[#2B3133] font-poppins text-sm font-medium">
                        Dosage
                      </span>
                      <span className="text-[#2B3133] font-poppins text-sm">
                        {items[idx]?.dosage || '2 per day'}
                      </span>
                    </div>
                    <div className="flex justify-between items-center py-2 border-b border-[#EDEEF0]">
                      <span className="text-[#2B3133] font-poppins text-sm font-medium">
                        Quantity
                      </span>
                      <span className="text-[#2B3133] font-poppins text-sm">
                        {i.qty}
                      </span>
                    </div>
                    <div className="flex justify-between items-center py-2">
                      <span className="text-[#2B3133] font-poppins text-sm font-medium">
                        Price
                      </span>
                      <span className="text-[#2B3133] font-poppins text-lg font-semibold">
                        {formatEuroNl(i.price)}
                      </span>
                    </div>
                  </div>
                </div>
              );
            })}

            <div className="bg-white rounded-xl border border-[#E5E7EB] p-4">
              <div className="space-y-3 mb-6">
                <div className="flex justify-between items-center">
                  <span className="text-[#2B3133] font-poppins text-sm font-medium">
                    Verzendkosten en fulfilment:
                  </span>
                  <span className="text-[#2B3133] font-poppins text-sm font-medium">
                    {formatEuroNl(shippingExVat)}
                  </span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-[#2B3133] font-poppins text-sm font-medium">
                    BTW (21%):
                  </span>
                  <span className="text-[#2B3133] font-poppins text-sm font-medium">
                    {formatEuroNl(vatOnShipping)}
                  </span>
                </div>
                <div className="flex justify-between items-center pt-2 border-t border-[#EDEEF0]">
                  <span className="text-[#2B3133] font-poppins text-lg font-semibold">
                    Total:
                  </span>
                  <span className="text-[#2B3133] font-poppins text-lg font-semibold">
                    {formatEuroNl(grandTotal)}
                  </span>
                </div>
              </div>
              <button
                onClick={handleApproveClick}
                disabled={loading || isApproved}
                className={`w-full h-12 rounded-full text-white font-poppins text-lg font-medium whitespace-nowrap text-center ${loading || isApproved ? 'bg-[#9BBFAD] cursor-not-allowed' : 'bg-[#119975]'}`}
              >
                {isApproved ? 'You have approved' : 'Approve'}
              </button>
            </div>
          </div>

          <div className="hidden md:block rounded-xl border border-[#E5E7EB] overflow-hidden bg-white mx-[-1px]">
            <div
              className="grid text-[#2B3133] font-poppins text-[14px] font-medium px-8"
              style={{
                gridTemplateColumns: '1fr 120px 80px 120px',
                columnGap: '12px',
                background: '#E8E8E8',
                height: '70px',
                alignItems: 'center',
              }}
            >
              <div
                className="md:border-r md:border-[#EDEEF0] pr-2 md:pr-4"
                style={{ paddingLeft: '44px' }}
              >
                SUPPLEMENT
              </div>
              <div className="font-normal">DOSAGE</div>
              <div className="text-center font-normal">QUANTITY</div>
              <div className="text-right">PRICE</div>
            </div>
            {items.map((i, idx) => {
              const itemApproved = isItemApproved(i);
              const supplementImage = getSupplementImage(i);
              const descriptionPreview = i.webInfo
                ? getDescriptionPreview(i.webInfo)
                : '';
              return (
                <div
                  key={i.id}
                  className={`grid items-center px-8 py-6 ${idx !== items.length - 1 ? 'border-b border-[#EDEEF0]' : ''} ${itemApproved || i.checked ? '' : 'opacity-50'}`}
                  style={{
                    gridTemplateColumns: '1fr 120px 80px 120px',
                    columnGap: '12px',
                  }}
                >
                  <div
                    className="flex items-center pr-2 md:pr-4 md:border-r md:border-[#EDEEF0]"
                    style={{ gap: '24px' }}
                  >
                    {!isApproved && isItemSelectable(i) && !itemApproved && (
                      <input
                        type="checkbox"
                        checked={i.checked}
                        onChange={() => toggle(i.id)}
                        className="w-5 h-5 accent-[#119975]"
                      />
                    )}
                    <div className="w-16 h-16 bg-[#F5F5F5] rounded-lg flex items-center justify-center shrink-0">
                      {supplementImage ? (
                        <img
                          src={supplementImage}
                          alt={i.brand}
                          className="w-full h-full object-contain p-2"
                        />
                      ) : (
                        <img
                          src={ProductImg}
                          alt="product"
                          className="w-full h-full object-contain p-2"
                        />
                      )}
                    </div>
                    <div
                      className="flex-1 pr-4 relative"
                      onMouseEnter={(e) => showDescriptionTooltip(i, e)}
                      onMouseLeave={() => setHoveredSupplementId(null)}
                    >
                      <h3
                        className={`text-[#2B3133] font-poppins text-[18px] font-semibold mb-0.5 transition-colors ${hasDescriptionBody(i.webInfo) ? 'cursor-pointer hover:text-[#119975]' : ''}`}
                      >
                        {i.brand}
                      </h3>
                      {i.packagingLine && (
                        <p className="text-gray-500 text-xs">{i.packagingLine}</p>
                      )}
                      {descriptionPreview && (
                        <p className="text-[#889696] font-poppins text-xs leading-relaxed line-clamp-3 mt-1 cursor-pointer">
                          {descriptionPreview}
                        </p>
                      )}
                    </div>
                  </div>
                  <div className="text-[#2B3133] font-poppins text-sm">
                    {i.dosage || '2 per day'}
                  </div>
                  <div className="text-[#2B3133] font-poppins text-sm text-center">
                    {i.qty}
                  </div>
                  <div className="text-[#2B3133] font-poppins text-[18px] font-semibold text-right">
                    {formatEuroNl(i.price)}
                  </div>
                </div>
              );
            })}

            <div
              className="grid items-center px-8 py-4 border-t border-[#EDEEF0]"
              style={{
                gridTemplateColumns: '1fr 120px 80px 120px',
                columnGap: '12px',
              }}
            >
              <div className="col-span-3 text-[#2B3133] font-poppins text-sm font-medium text-right pr-4">
                Verzendkosten en fulfilment:
              </div>
              <div className="text-[#2B3133] font-poppins text-sm font-medium text-right">
                {formatEuroNl(shippingExVat)}
              </div>
            </div>
            <div
              className="grid items-center px-8 pt-0 pb-4"
              style={{
                gridTemplateColumns: '1fr 120px 80px 120px',
                columnGap: '12px',
              }}
            >
              <div className="col-span-3 text-[#2B3133] font-poppins text-sm font-medium text-right pr-4">
                BTW (21%):
              </div>
              <div className="text-[#2B3133] font-poppins text-sm font-medium text-right">
                {formatEuroNl(vatOnShipping)}
              </div>
            </div>
            <div className="border-t border-[#EDEEF0]">
              <div className="flex justify-end px-8 py-6 bg-white">
                <div
                  className="grid items-center"
                  style={{
                    gridTemplateColumns: 'auto 220px',
                    columnGap: '0px',
                  }}
                >
                  <div className="text-[#2B3133] font-poppins text-[18px] font-semibold text-right">
                    Total:
                  </div>
                  <div className="text-[#2B3133] font-poppins text-[18px] font-semibold text-right">
                    {formatEuroNl(grandTotal)}
                  </div>
                </div>
              </div>
              <div className="border-t border-[#EDEEF0]" />
              <div className="flex justify-end px-8 py-6">
                <button
                  onClick={handleApproveClick}
                  disabled={loading || isApproved}
                  className={`px-10 h-12 rounded-full font-poppins text-lg font-medium w-[260px] whitespace-nowrap text-center ${loading || isApproved ? 'bg-[#9BBFAD] text-white cursor-not-allowed' : 'bg-[#119975] text-[#FFEBDB]'}`}
                >
                  {isApproved ? 'You have approved' : 'Approve'}
                </button>
              </div>
            </div>
          </div>
        </>
      )}
      {hoveredSupplementId &&
        hasDescriptionBody(
          items.find((s) => s.id === hoveredSupplementId)?.webInfo
        ) &&
        createPortal(
          <div
            className="fixed z-[9999] w-[500px] max-w-[90vw] bg-white rounded-lg shadow-xl border border-gray-200 p-4 max-h-[70vh] overflow-y-auto"
            style={{
              top: `${tooltipPosition.top}px`,
              left: `${tooltipPosition.left}px`,
            }}
            onMouseEnter={() => setHoveredSupplementId(hoveredSupplementId)}
            onMouseLeave={() => setHoveredSupplementId(null)}
          >
            <div
              className="text-sm text-[#2B3133] font-poppins tooltip-content"
              dangerouslySetInnerHTML={{
                __html: stripWebInfoTitle(
                  items.find((s) => s.id === hoveredSupplementId)?.webInfo || ''
                ),
              }}
              style={{
                fontSize: '13px',
                lineHeight: '1.6',
              }}
            />
            <style>{`
            .tooltip-content table {
              width: 100%;
              border-collapse: collapse;
              margin: 8px 0;
            }
            .tooltip-content table td,
            .tooltip-content table th {
              border: 1px solid #ddd;
              padding: 6px;
              text-align: left;
            }
            .tooltip-content table strong {
              font-weight: 600;
            }
            .tooltip-content b {
              font-weight: 600;
            }
            .tooltip-content p {
              margin: 8px 0;
            }
          `}</style>
          </div>,
          document.body
        )}

      {showConfirmModal && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50"
          onClick={handleCancelApprove}
        >
          <div
            className="bg-white rounded-2xl p-6 md:p-8 max-w-lg w-full mx-4 shadow-xl"
            onClick={(e) => e.stopPropagation()}
          >
            <h2 className="text-[#2B3133] font-poppins font-semibold text-xl mb-4">
              Confirm Approval
            </h2>
            <p className="text-[#2B3133] font-poppins text-sm mb-6">
              Je staat op het punt je bestelling definitief te plaatsen. Daarna
              word je doorgestuurd naar het betaalscherm.
            </p>
            <div className="grid grid-cols-2 gap-3">
              <button
                type="button"
                onClick={handleCancelApprove}
                disabled={loading}
                className="h-12 min-w-0 px-3 border border-gray-300 text-gray-700 rounded-full font-poppins text-sm font-medium whitespace-nowrap flex items-center justify-center hover:bg-gray-50 transition disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Niet goedkeuren
              </button>
              <button
                type="button"
                onClick={handleApprove}
                disabled={loading}
                className="h-12 min-w-0 px-3 bg-[#119975] text-white rounded-full font-poppins text-sm font-medium whitespace-nowrap flex items-center justify-center hover:opacity-90 transition disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {loading ? 'Goedkeuren...' : 'Bestelling goedkeuren'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
